import * as THREE from 'three';

const OBJ_COUNT = 3000;

/**
 * Generate deterministic/shared transformation properties for the instanced meshes
 * so both Scene A and Scene B have objects in identical positions and orientations.
 */
function generateMeshTransforms(count = OBJ_COUNT) {
  const transforms = [];
  for (let i = 0; i < count; i += 1) {
    transforms.push({
      position: {
        x: Math.random() * 10000 - 5000,
        y: Math.random() * 6000 - 3000,
        z: Math.random() * 8000 - 4000,
      },
      rotation: {
        x: Math.random() * 2 * Math.PI,
        y: Math.random() * 2 * Math.PI,
        z: Math.random() * 2 * Math.PI,
      },
      scale: Math.random() * 200 + 100,
    });
  }
  return transforms;
}

// Shared mesh properties across scenes for seamless alignment during transition
const sharedMeshProps = generateMeshTransforms(OBJ_COUNT);

/**
 * Creates an InstancedMesh with icosahedron geometry
 */
function createInstancedMesh(material, needsAnimatedColor = false) {
  const size = 0.3;
  const geometry = new THREE.IcosahedronGeometry(size, 1);
  const mesh = new THREE.InstancedMesh(geometry, material, OBJ_COUNT);

  const dummy = new THREE.Object3D();
  const color = new THREE.Color();

  for (let i = 0; i < OBJ_COUNT; i += 1) {
    const props = sharedMeshProps[i];
    dummy.position.set(props.position.x, props.position.y, props.position.z);
    dummy.rotation.set(props.rotation.x, props.rotation.y, props.rotation.z);
    dummy.scale.set(props.scale, props.scale, props.scale);
    dummy.updateMatrix();

    mesh.setMatrixAt(i, dummy.matrix);

    if (needsAnimatedColor) {
      mesh.setColorAt(i, color.setScalar(0.1 + 0.9 * Math.random()));
    }
  }

  mesh.instanceMatrix.needsUpdate = true;
  if (needsAnimatedColor && mesh.instanceColor) {
    mesh.instanceColor.needsUpdate = true;
  }

  return mesh;
}

/**
 * Initializes and returns an FX Scene with camera, lights, mesh, and offscreen FBO
 */
export function getFXScene({
  renderer,
  material,
  clearColor = 0x000000,
  needsAnimatedColor = false,
}) {
  let width = window.innerWidth;
  let height = window.innerHeight;

  const camera = new THREE.PerspectiveCamera(50, width / height, 1, 10000);
  camera.position.z = 2000;

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(clearColor, 0.0002);

  const hemiLight = new THREE.HemisphereLight(0xffffff, 0x555555, 1.0);
  scene.add(hemiLight);

  const mesh = createInstancedMesh(material, needsAnimatedColor);
  scene.add(mesh);

  const fbo = new THREE.WebGLRenderTarget(width, height, {
    minFilter: THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    format: THREE.RGBAFormat,
  });

  const rotationSpeed = new THREE.Vector3(0.1, -0.2, 0.15);

  const update = (delta) => {
    mesh.rotation.x += delta * rotationSpeed.x;
    mesh.rotation.y += delta * rotationSpeed.y;
    mesh.rotation.z += delta * rotationSpeed.z;

    if (needsAnimatedColor && material.color) {
      material.color.setHSL(0.1 + 0.5 * Math.sin(0.0002 * Date.now()), 1, 0.5);
    }
  };

  const render = (delta, rtt) => {
    update(delta);

    renderer.setClearColor(clearColor);

    if (rtt) {
      renderer.setRenderTarget(fbo);
      renderer.clear();
      renderer.render(scene, camera);
    } else {
      renderer.setRenderTarget(null);
      renderer.render(scene, camera);
    }
  };

  const onResize = (newWidth, newHeight) => {
    width = newWidth;
    height = newHeight;
    camera.aspect = width / height;
    camera.updateProjectionMatrix();
    fbo.setSize(width, height);
  };

  const dispose = () => {
    fbo.dispose();
    mesh.geometry.dispose();
  };

  return {
    scene,
    camera,
    mesh,
    fbo,
    render,
    update,
    onResize,
    dispose,
  };
}
